/** @module csi/view/_query/Result */
define([
	"dojo/_base/declare",
	"dojo/when",
	"csi",
	"csi/_base/underscore",
	"csi/constants",
	"csi/def",
	"csi/html",
	"csi/reflection",
	"csi/store/ValuesFromPairs",
	"csi/store/ValuePairs",
	"csi/widget/Automation",
	"csi/widget/InlineActionMenu",
	"csi/xml"
], function(declare, when, csi, _, constants, def, html, reflection, ValuesFromPairs, ValuePairs, Automation, InlineActionMenu, xml) {

	/**
	 * A function returning true or false indicating if result should be considered as closest result
	 * @callback closestResultFilter
	 * @param {Result} qr
	 * @return {boolean}
	 */

	const ACTIONS_EXCLUDED_FROM_ELLIPSES = new Set([
		"ShapeSizeChartDimensionDelete",
		"DataSheetComment",
		"Dnd",
		"Delete",
		"Edit",
		"Copy",
		"MoveUp",
		"MoveDown",
		"DirectEditAI",
		"LinkActual",
		"CreateStockOrder",
		"3DPreview",

		// BO Attributes table
		"DeleteUOMCustomAttribute",
		"ManageAggregateStore",
		"MarkForDeletionCustomAttribute",
		"MarkForDeletionUOMCustomAttribute",
		"UnmarkForDeletionCustomAttribute",
		"UnmarkForDeletionUOMCustomAttribute",

		// IngredientItem and FlatIngredientItem table
		"IngredientItemInspect",
	]);

	function _getMenuActions(/*Actions*/actions) {
		// summary: extract actions that should appear in "..." menu
		var menu = [];

		_.each(actions.getActions(), function(a) {
			if (a.requirePrivateNode && a.privateNode) {
				menu.push(a);
			}
			a.requirePrivateNode = false;
		});

		var singleAction = (menu.length == 1) ? menu[0] : null;
		if (singleAction && !singleAction.bEllipses && (singleAction.m_typeControl != def.ACTION_LINK)) {
			// privateNode is a reference to the node, so the node itself will not be deleted
			html.setVisible(singleAction.privateNode, true);
			singleAction.privateNode = null;
			return null;
		}

		return menu.length ? menu : null;
	}

	function _updateGroupCount(/*QueryResult*/qr, /*Number*/count, /*Boolean*/bIncrement) {
		var store, node, x;

		while (qr.level.groupSpec) {
			store = qr.parentResult.store;
			node = qr.getNode();
			x = store.countProperty;

			// if setting an explicit count on the lowest group level, then still need to increment the count on the first group level;
			// so turn explicit count into increment
			if (!bIncrement) {
				count -= node[x];
				bIncrement = true;
			}

			node[x] += count;
			store.put(node);

			// reset cached list of child urls - will be calculated on demand next time accessed
			qr.childResultUrls = null;

			// iterate up the tree
			qr = qr.parentResult;
		}

		// reset for root result too
		qr.childResultUrls = null;
	}

	var _Result = declare(null, {
		// Boolean: true if result represents action
		//bAction: false,

		// Boolean: true if result came from a comparison view
		//bCompareNode: false,

		// Boolean: true if domNode for result is hidden (under collapsed node)
		//bHidden: false,

		// Boolean: true if not displaying all child results
		//bMore: false,

		/** @property {boolean} bNewlyCreated - if true, then query result represents the “Newly Created” group */
		//bNewlyCreated: false,

		// Boolean: true if result represents node
		//bResultNode: false,

		// Boolean: true if result represents report
		//bReport: false,

		// QueryResult[]: list of results whose parentResult is this
		//childResults: null,

		/** @property {string[]} childResultUrls - cached list of result urls used for reporting - generated on demand for root and first level groups */
		//childResultUrls: null,

		/** @property {string|undefined} comparisonKey - key used to find comparison result */
		//comparisonKey: undefined,

		/** @property {Result|undefined|null} comparisonResult - result used for comparison */
		//comparisonResult: undefined,

		/** @property {boolean} generateReport - if true, then report row calculations are required for this result */
		//generateReport: false,

		// Any: if group result, then store real value of group which can be different than what result node has for value in case of ref attributes
		// groupValue gets its value from _url from the group Node. _url is only set when we have defaultStoresValue defined on QueryLevel.
		//groupValue: null,

		// Number: level index for result starting with 0; not stored on QueryLevel because Relationship could repeat (tree structure)
		//hl: 0,

		// String: unique id for results in view - at main level is same as "url"
		id: null,

		// QueryLevel: configuration for level at which result
		level: null,

		// Number: number of children from store displayed in view
		nChildren: 0,

		// QueryResult: parent item whose query resulted in this item
		//parentResult: null,

		// String: url of parent context - skips group results and will default to root view URL if no parent result
		//parentUrl: null,

		// Values|ValuePairs: value (pairs) store holding results to help with auto-expanding groups; only applicable at root level
		//rootValueStore: null,

		// Store: data store holding child results
		//store: null,

		// String: url for result (not necessarily unique in cases where root node of view ends up as result in view - AddPath Path="")
		url: null,

		// String: if action result, then this is url for result node which would be shown in place of action is more results were queried from parent
		//urlResultForAction: null,

		// Boolean: flag indicating context was set to read-only for this result
		//_bReadOnly: false,

		constructor(/*Object?*/params) {
			// params can be null for call from api.js->excludeActionFromElipses(), used to add action to ACTIONS_EXCLUDED_FROM_ELLIPSES 
			if (!params) {
				return;
			}

			_.extend(this, params);

			// initialize group value for ref attributes if known - it is possible node is null when creating results
			// for "More" excel results (not sure about other cases)
			const groupSpec = this.bResultNode ? this.level.groupSpec : null;
			const node = this.getNode();

			if (node && groupSpec && xml.attributeTypeIsRef(groupSpec.type)) {
				this.groupValue = node[this.parentResult.store.urlProperty];
			}

			// special treatment for grouped results
			var qrParent = this.parentResult;
			if (qrParent) {
				var parentGroupSpec = qrParent.level.groupSpec;
				if (parentGroupSpec) {
					// if nested group result, then parentUrl should reference root result url
					this.parentUrl = qrParent.parentUrl;

					// fill group value of parent based on actual nodes rather than ValuesByXML results;
					// in 'ref' case, we want the URL (ref) rather than the name (string)
					if (node && this.bResultNode && !this.bReport) {
						var ctx = this.getContext();

						while (parentGroupSpec) {
							if (qrParent.groupValue == null) {
								if (ctx.push(parentGroupSpec.heading.m_path)) {
									qrParent.groupValue = ctx.getNode()[parentGroupSpec.heading.m_id];
									ctx.pop();
								}
							}

							qrParent = qrParent.parentResult;
							parentGroupSpec = qrParent && qrParent.level.groupSpec;
						}
					}
				}
			}

			// id must be unique in view, even if same url appears more than once (such as in hierarchical structure), so id should be
			// combination of url and parent id (which is itself combination of parent urls); for groups, their id should be unique (even across views),
			// but also predictable so that when the view is refreshed the same groups can be expanded
			if (!this.id) {
				if (groupSpec) {
					var groupValue = this.groupValue;

					if (groupValue == null) {
						groupValue = this.getGroupValue();
					}

					if (groupValue != null) {
						this.id = this.level.qm.get("groupUniqueId") + groupValue;

						// for second level groups, include id of parent group result
						if (this.parentResult.level.groupSpec) {
							this.id += "/" + this.parentResult.id;
						}
					}
				} else {
					this.id = this.url;

					if (qrParent && !qrParent.isRoot() && !qrParent.isGroup()) {
						this.id += "/" + qrParent.id;
					}
				}
			}
		},

		addChild(/*Result*/qr, /*Result?*/qrNext) {
			// summary: add child result to this result
			var results = this.childResults = this.childResults || [];

			if (qr.bResultNode && !qr.bCompareNode) {
				++this.nChildren;
			}

			if (qr.isSiblingReport()) {
				results.unshift(qr);
			} else if (qrNext) {
				var i = _.indexOf(results, qrNext);
				results.splice(i, 0, qr);
			} else {
				results.push(qr);
			}
		},

		canAggregate() {
			// summary: return true if this result is available for selection
			return this.bResultNode && !this.bReport && this.level.bCanAggregate;
		},

		containsGroups() {
			// summary: return true if child results will be groups
			var level = this.level.childLevel;
			return level && !!level.groupSpec;
		},

		getAutomationId() {
			// summary: return unique string for result to provide context for automation
			if (this.isRoot()) {
				return "root";
			}

			if (this.groupValue == null) {
				return this.url;
			}

			var id = String(this.groupValue);

			if (this.parentResult && (this.parentResult.groupValue != null)) {
				id += "-" + String(this.parentResult.groupValue);
			}

			return id;
		},

		getChildContext() {
			// summary: return context for child items - if this result is a group, then use parentUrl as context parent
			return this.level.childLevel.getContext(csi.EMPTY_URL, this.level.groupSpec ? this.parentUrl : this.url);
		},

		getChildCountLimit() {
			// summary: return max number of items allowed per level; if group level, then look for first non-grouped level to find limit
			var level = this.level.childLevel;

			while (level.groupSpec) {
				level = level.childLevel;
			}

			return level.getResultLimit();
		},

		getChildDisplayCount() {
			// summary: return number of children currently displayed
			return this.nChildren;
		},

		/**
		 * Return child result where to insert new results, depending on if results are ordered or not
		 * @return {QueryResult | null}
		 */
		getChildResultWhereToInsert() {
			const bOrdered = this.hasOrderedChildren();
			const {childResults} = this;

			if (childResults) {
				for (const qr of childResults) {
					if (qr.bAction || (qr.bResultNode && !bOrdered)) {
						return qr;
					}
				}
			}

			return null;
		},

		/**
		 * Return result nodes of children (not reports or actions); if children are groups, then return all their child results.
		 * If any results are not yet known, then return null. It is possible that some results are null though that have not yet been cached.
		 * @param {boolean} bRepeat - if level repeats, then recurse to find children (depth first)
		 * @return {string[] | null}
		 */
		getChildResultUrls(bRepeat = false) {
			const {bResultNode, childResults, level: {childLevel}} = this;

			// if report or action node, then return empty array since they will never have any children
			if (!this.isRoot() && !bResultNode) {
				return [];
			}

			// if leaf node and no store, then no results
			const store = this.getStore();
			if (!store) {
				return null;
			}

			// if group and not loaded, then return urls stored on group
			if (this.isGroup() && !store.isLoaded()) {
				return this.getGroupUrls();
			}

			let results = store.getResults();

			if (this.containsGroups()) {
				let {childResultUrls} = this;

				if (!childResultUrls) {
					// add results from child groups
					const {refsProperty} = store;
					const mapResults = csi.hashArray(results, null, Boolean, {});

					childResultUrls = [];

					// iterate over displayed results
					if (childResults) {
						for (const qr of childResults) {
							if (qr.bResultNode) {
								// it is possible an unexpanded child group does not yet have urls - in this case, we have to abandon the whole process
								// as we do not know the full list of urls
								const urls = qr.getChildResultUrls();
								if (!urls) {
									return null;
								}

								childResultUrls = childResultUrls.concat(urls);
								delete mapResults[qr.url];
							}
						}
					}

					// iterate over non-displayed results (when More exists)
					for (const url in mapResults) {
						const item = store.get(url);
						const urls = item[refsProperty];
						if (urls) {
							childResultUrls = childResultUrls.concat(urls);
						} else {
							return null;
						}
					}

					// cache result until there is a new child or removed child
					this.childResultUrls = childResultUrls;
				}

				return childResultUrls;
			} else if (bRepeat && childResults && childLevel?.isRepeatLevel()) {
				// iterate over children as level repeats
				results = [];

				for (const qr of childResults) {
					if (qr.bResultNode) {
						// track result and any child results
						const urls = qr.getChildResultUrls() || [];
						results = [...results, qr.url, ...urls];
					}
				}
			}

			return results;
		},

		/**
		 * Return list of result urls displayed under current result - skip over groups if any
		 * @return {cnl[]}
		 */
		getDisplayedChildResultUrls() {
			const {childResults} = this;

			if (childResults) {
				return childResults.flatMap((qr) => {
					if (qr.isGroup()) {
						return qr.getDisplayedChildResultUrls();
					} else if (qr.bResultNode) {
						return qr.url;
					}

					return [];
				});
			}

			return [];
		},

		getChildTotalCount() {
			// summary: return total number of children - if total is unknown, or using client-side querying, then assume answer is the number of displayed results
			const {store} = this;
			if (!store) {
				return 0;
			}

			if (store.bSimpleQueryEngine || !store.getTotal) {
				return this.getChildDisplayCount();
			}

			return store.getTotal();
		},

		getChildDisplayedItemCount() {
			// summary: same as getChildTotalCount except for results with group children; in that case we need to sum the counts of all displayed groups
			var store = this.store;
			var nChildren = this.getChildDisplayCount();
			return (store && store.getResultTotal) ? store.getResultTotal(nChildren) : nChildren;
		},

		getChildTotalItemCount() {
			// summary: same as getChildTotalCount except for results with group children; in that case we need to sum the counts of all groups
			if (this.isGroup(true)) {
				return this.getGroupCount();
			}

			const {store} = this;
			return (store && store.getResultTotal) ? store.getResultTotal() : this.getChildTotalCount();
		},

		getContext() {
			// summary: return context for item
			const ctx = this.level.getContext(this.url, this.parentUrl);
			ctx.queryResult = this;
			return ctx;
		},

		/**
		 * Return first child result (skipping any report row)
		 * @param {boolean} [bResultsOnly] - if true, then look for first result node (skipping a possible report result)
		 * @return {QueryResult | null}
		 */
		getFirstChildResult(bResultsOnly) {
			const {childResults} = this;

			if (childResults) {
				for (const qr of childResults) {
					if (!bResultsOnly || qr.bResultNode) {
						return qr;
					}
				}
			}

			return null;
		},

		getGroupParents() {
			// summary: return all group results containing this result
			var qr = this.parentResult;
			var a = [];

			while (qr && qr.level.groupSpec) {
				a.push(qr);
				qr = qr.parentResult;
			}

			return a.length ? a : null;
		},

		/**
		 * If a group result, then return list of URLs contained in group. This will only be known if auto-expanding the group, or expanding the level.
		 * @return {string[] | null} 
		 */
		getGroupUrls() {
			return this.isGroup(true) ? this.getNode()[this.parentResult.store.refsProperty] : null;
		},

		/**
		 * If a group result, then return count of items in the group (otherwise 0)
		 * @return {number | null} 
		 */
		getGroupCount() {
			if (this.isGroup(true)) {
				const prop = this.parentResult.store?.countProperty;
				return prop ? this.getNode()[prop] : null;
			}
			return 0;
		},

		/**
		 * If a group result, then return name of group
		 * @return {string | null}
		 */
		getGroupName() {
			return this.isGroup(true) ? this.getNode()[this.parentResult.store.textProperty] : null;
		},

		/**
		 * If a group result, then return group value. The value could be different from groupValue stored on result if grouping by ref;
		 * in that case, groupValue is a ref (if group is expanded) as opposed to a string (from store)
		 * @return {any}
		 */
		getGroupValue() {
			return this.isGroup(true) && this.parentResult.store ? this.getNode()[this.parentResult.store.valueProperty] : null;
		},

		/**
		 * Return parent group of result, if any
		 * @param {boolean} [bOmitThis] - if true, then do not test to see if this result is a group
		 * @return {Result}
		 */
		getParentGroup(bOmitThis) {
			if (!bOmitThis && this.isGroup()) {
				return this;
			}

			return this.parentResult ? this.parentResult.getParentGroup() : null;
		},

		/**
		 * Return values of parent groups
		 * @return {any[]}
		 */
		getParentGroupValues() {
			const groups = this.getGroupParents() || [];
			return groups.map((qr) => qr.getGroupValue());
		},

		/**
		 * Return query result for Newly Created group; if two levels of grouping, then return lower group result
		 * @param {boolean} [bFirst] - if true, then do not recursively look for newly created result (only get first level group)
		 * @return {QueryResult | null}
		 */
		getNewlyCreatedQueryResult(bFirst) {
			let qr = _.findWhere(this.childResults, {bNewlyCreated: true});

			if (!bFirst && qr && qr.containsGroups()) {
				qr = qr.getNewlyCreatedQueryResult(true);
			}

			return qr;
		},

		/**
		 * Return closest result at same level; if only one result under parent, then look under parent siblings
		 * @param {closestResultFilter} [parentFilter] - optional function allowing filtering of parent results to consider
		 * @return {Result}
		 */
		getClosestResult(parentFilter) {
			var parent = this.parentResult;
			if (!parent) {
				return null;
			}

			function getChildren(candidateResult) {
				// only return children if result passes parent filter function
				return (parentFilter && !parentFilter(candidateResult) ? null : candidateResult.childResults) || [];
			}

			var children = getChildren(parent);
			var index;
			var qr;

			// look for siblings, next then previous
			if (children.length > 1) {
				index = _.indexOf(children, this);

				if (qr = children[index + 1] || children[index - 1]) {
					return qr;
				}
			}

			// look for results under parent siblings
			var grandparent = parent.parentResult;
			if (grandparent) {
				children = grandparent.childResults;
				index = _.indexOf(children, parent);

				var grandchildren;
				var n = children.length;
				var i;

				// look through next parent siblings
				for (i = index + 1; i < n; ++i) {
					grandchildren = getChildren(children[i]);

					if (qr = grandchildren.first()) {
						return qr;
					}
				}

				// look through previous parent siblings
				for (i = index - 1; i >= 0; --i) {
					grandchildren = getChildren(children[i]);

					if (qr = grandchildren.last()) {
						return qr;
					}
				}
			}

			return null;
		},

		/**
		 * Return query object to apply to child results
		 * @param {boolean} [omitGroupQuery] - set to true to omit querying by group value(s)
		 * @return {Record<string,any>}
		 */
		getQuery(omitGroupQuery) {
			var childLevel = this.level.childLevel;
			if (!childLevel) {
				return null;
			}

			// if client query is specified, use it alone; currently this is only in use in one place, and is not concerned with grouping or other ad-hoc queries
			if (childLevel.clientAdHocQuery) {
				return childLevel.clientAdHocQuery;
			}

			var qr = this;
			var q = {};
			var queryKey;
			var groupValue;

			// add ad-hoc query filters
			if (childLevel.bMain || childLevel.groupSpec) {
				_.extend(q, childLevel.qm.get("queryForServer"));
			}

			// add query filters for grouping
			if (!omitGroupQuery) {
				while (qr && qr.level.groupSpec) {
					queryKey = qr.level.groupSpec.queryKey;
					groupValue = qr.getGroupValue();
					q[queryKey] = [groupValue];
					qr = qr.parentResult;
				}
			}

			return q;
		},

		/**
		 * Return set of new actions based on the context
		 * @param {Context} ctx - current context
		 * @param {boolean} [bMoreResults] - if true, then there are still more results to view
		 * @param {Level} [targetLevel] - Gives the current hierarchy level we are in 
		 * @param {boolean} [bToolbarButton] - if true, then toolbar button is enabled
		 * @param {Action[]} [placeholderActions] - if placeholderActions exist, use those instead of NewActions
		 * @return {Action[]}
		 */
		getNewActions(ctx, bMoreResults, targetLevel, bToolbarButton, placeholderActions) {
			// if child level is read-only, then no actions
			if (ctx.isReadOnly()) {
				return [];
			}

			const {groupValue, level, parentUrl, url} = this;
			const {cache, queryResult, view} = ctx;
			const childLevel = targetLevel || level.childLevel;
			const {actionFilterFunc, bOrdered, newActions, parentAttribute} = childLevel;
			const bGrouped = !!level.groupSpec;

			// if results are ordered and (there are still more results to view and custom view preference sort does not exists), then no actions
			if (bMoreResults && bOrdered && !view.get("customViewSortExists")) {
				return [];
			}

			// if level has access function, check it now - ctx node should be this result's node (ctx.getNode() === this.getNode())
			const parent = cache.getNode(groupValue) || this.getNode();

			if (!bToolbarButton && !placeholderActions && childLevel.shouldDisableActions(ctx, null, parent)) {
				return [];
			}

			// ensure queryResult on context is updated, and restore to previous value afterwards
			ctx.queryResult = this;

			// evaluate actions with ctx where this result's node is the parent (ctx.getParent() === this.getNode())
			ctx.pushParams({
				urlNode: csi.EMPTY_URL,
				urlParent: bGrouped ? parentUrl : url,
				idRelation: parentAttribute,
			});

			const actions = placeholderActions ?? newActions ?? [];
			const validatedActions = actions.filter((a) => {
				let b;

				if (ctx.push(a.m_path)) {
					b = a.validate(ctx) && a.hasAccess(ctx);

					// do not allow dividers to be created when results are grouped
					// FIX - not sure of a better way to do this, so just did it here
					if (b && bGrouped && reflection.isDividerType(a.m_typeNode)) {
						b = false;
					} else if (b) {
						b = actionFilterFunc(ctx, this, a);
					}

					ctx.pop();
				}

				return b;
			});

			ctx.pop();
			ctx.queryResult = queryResult;

			return validatedActions;
		},

		/**
		 * Return next or previous sibling result 
		 * @param {QueryResult} qrChild 
		 * @param {boolean} [bPrevious]
		 * @return {QueryResult | null}
		 */
		getNextResult(qrChild, bPrevious) {
			const {childResults} = this;
			const n = childResults.indexOf(qrChild);
			return childResults[n + (bPrevious ? -1 : 1)];
		},

		getNextResultUrl(/*String?*/url, /*Boolean*/bPrevious) {
			// summary: return url of next result if given existing url; otherwise return result that is not yet displayed
			var urls = this.store && this.store.getResults && this.store.getResults();
			if (!urls) {
				// FIX: run query to get results if not Nodes store?
				return null;
			}

			var n = this.getChildDisplayCount();

			if (url) {
				n = _.indexOf(urls, url) + (bPrevious ? -1 : 1);
			}

			return urls[n];
		},

		getNode() {
			// summary: return store item for result
			var store = this.parentResult ? this.parentResult.store : null;
			if (store) {
				return store.get(this.url);
			}

			return this.getContext().getNode();
		},

		getReport() {
			// summary: definition for report is at child level if result node; otherwise get report from current level (assuming this is report node)
			return (this.bResultNode || !this.bReport) ? this.level.childLevel.report : this.level.report;
		},

		getResultItemCount(/*QueryResults?*/results) {
			// summary: only called on root result and return total number of results in view
			console.assert(this.isRoot());

			if (this.store && this.containsGroups()) {
				return this.store.getResultTotal();
			}

			return (results && results.total) || 0;
		},

		getResultPathUrls(/*Context?*/ctx) {
			// summary: return list of urls referenced by resultPath
			var level = this.level.childLevel;
			if (!level || !level.resultPath) {
				return null;
			}

			ctx = ctx || this.getContext();
			return ctx.cache.getLimitPathUrls(ctx.getNodeURL(), level.resultPath);
		},

		/**
		 * Return actual node represented by this result - usually same as getNode except in case of action result where there are more nodes to load
		 * @return {Node} 
		 */
		getResultNode() {
			if (this.bResultNode) {
				return this.getNode();
			}

			if (this.urlResultForAction) {
				return this.parentResult.store.get(this.urlResultForAction);
			}

			return null;
		},

		/**
		 * Return actual url represented by this result - usually same as getNode except in case of action result where there are more nodes to load
		 * @return {string} 
		 */
		getResultUrl() {
			return this.bResultNode ? this.url : this.urlResultForAction;
		},

		getRootResult() {
			// summary: traverse parentResult to top
			var p = this;

			while (p.parentResult) {
				p = p.parentResult;
			}

			return p;
		},

		getStore(/*Object?*/params) {
			// summary: return store to generate child results
			var level = this.level;
			var childLevel = level.childLevel;
			var qm = level.qm;

			if (childLevel && !this.store && xml.isValidURL(this.url)) {
				// if printing, lookup store in source view
				var sourceView = qm.printSourceView;
				if (sourceView && !sourceView.bPrintResetCache) {
					var sourceResult = sourceView.getQueryResult(this.id);
					if (sourceResult && sourceResult.store) {
						this.rootValueStore = sourceResult.rootValueStore;
						return this.store = sourceResult.store;
					}
				}

				var bRoot = this.isRoot();
				var groupSpec = level.groupSpec;
				var groupSpec1 = childLevel.groupSpec;
				var ctx;
				var store;

				// if result is grouped, then context should be that of root result; otherwise return context of this result
				if (groupSpec) {
					ctx = level.getContext(this.parentUrl);
				} else {
					ctx = this.getContext();
				}

				if (groupSpec1) {
					var valuesStoreOptions = {
						bBlanks: true,
						bCounts: true,
						ctx: ctx
					};

					var grandChildLevel = childLevel.childLevel;
					var groupSpec2 = grandChildLevel && grandChildLevel.groupSpec;
					var groupStoreClass = childLevel.groupStoreClass;
					var mainStore = qm.get("store");
					var valuesStore;

					if (bRoot && groupSpec2) {
						// query for value pairs so both group levels can be populated with one request, but nodes will be queried later per group
						valuesStore = new ValuesFromPairs({
							_store: this.getRootValueStore(),
							filterSpec: null,
							spec: groupSpec1,
							valuesStoreOptions: valuesStoreOptions
						});
					} else if (bRoot) {
						// if only one level grouping, then use Values store; if auto-expanding, then request for CNLs as part of Values request
						valuesStore = this.getRootValueStore();
					} else {
						// second level of grouping, so construct from root result's rootValueStore
						console.assert(!!this.parentResult.rootValueStore);

						valuesStore = new ValuesFromPairs({
							_store: this.parentResult.rootValueStore,
							filterSpec: groupSpec,
							spec: groupSpec1,
							valuesStoreOptions: valuesStoreOptions
						});
					}

					// newly created group store should be loaded by default
					if (this.bNewlyCreated) {
						params = params || {};
						params.resultUrls = [];
					}

					store = new groupStoreClass(_.extend({
						cachingStore: ctx.cache,
						spec: groupSpec1,
						typeNode: mainStore.typeNode,
						ungroupedStore: mainStore,
						valuesStore: valuesStore,
						defaultValuesStore: childLevel.groupDefaultsStore,
						onDefaultGroup: _.bind(childLevel.onDefaultGroup, childLevel)
					}, params));
				} else {
					if (groupSpec) {
						// dividers should not be shown in groups
						params = params || {};
						params.dividerType = null;

						// if GroupNode has CNLs for children, then use them
						params.resultUrls = this.getGroupUrls();
					}

					store = childLevel.createStore(ctx, false, params);
				}

				this.store = store;
			}

			return this.store;
		},

		/**
		 * Create value pairs store to determine groups
		 * @param {boolean} bQueryCnls - indicate if need to know CNLs per group; pass true if expanding all child groups at once
		 * @return {ValuePairs}
		 */
		getRootValueStore() {
			var qr = this;

			// go to root result
			while (qr.parentResult) {
				qr = qr.parentResult;
			}

			var childLevel = qr.level.childLevel;
			var groupSpec1 = childLevel.groupSpec;
			var grandChildLevel = childLevel.childLevel;
			var groupSpec2 = grandChildLevel && grandChildLevel.groupSpec;
			var mainStore = childLevel.qm.get("store");
			var valueStoreOptions = {
				bBlanks: true,
				bCounts: true,
				ctx: this.getContext()
			};

			var valueStore;

			console.assert(!!groupSpec1);

			// Retrieve not only COUNT but also list of URLs. This is required for "Auto Expand" and for "Newly Created" groups (C8-13824)
			valueStoreOptions.requestParameters = {
				[constants.QRY_VALUE_CNL_LIMIT]: constants.QUERY_RESULT_LIMIT
			};

			if (groupSpec2) {
				valueStore = mainStore.getValuePairsStore(groupSpec1, groupSpec2, valueStoreOptions);
			} else {
				valueStore = mainStore.getValuesStore(groupSpec1, valueStoreOptions);
			}

			return qr.rootValueStore = valueStore;
		},

		hasChildren(/*Context*/ctx) {
			// summary: return false if no possibility of there being children in current context; true otherwise, whether
			// it is known to have children or cannot be known without a query
			const {bAction, level, store, url} = this;
			const {childLevel, groupSpec, hasChildrenTest} = level;

			// check for infinite loop
			let parent = this.parentResult;
			while (parent) {
				//if the parent is the root, then don't consider as infinite loop
				if (!parent.isRoot() && parent.url === url) {
					return false;
				}
				parent = parent.parentResult;
			}

			if (!childLevel || bAction) {
				return false;
			}

			// report rows (that are not combined with group nodes) have no children
			if (this.isSiblingReport()) {
				return false;
			}

			// if there are new actions, then return true as they will be rendered as children
			if (this.getNewActions(ctx).length) {
				return true;
			}

			// if group node, then look at store to see if count is already known; if not, assume it will have children when queried
			if (groupSpec) {
				const total = store && store.getTotal();
				return (total == null) || (total > 0);
			}

			// call custom function to test for children without querying
			if (hasChildrenTest) {
				return hasChildrenTest(ctx);
			}

			// check for children stored in one of node's attributes
			if (childLevel.resultPath) {
				const urls = this.getResultPathUrls(ctx);
				return urls && urls.length;
			}

			return true;
		},

		hasMoreChildren() {
			// summary: return true if there are more results in store that have not yet been queried
			return this.bMore;
		},

		/**
		 * Return true if result is to be included in the total and display counts
		 * @return {boolean}
		 */
		includeInCount() {
			const {bCompareNode, bResultNode, level, url} = this;

			if (level.bMain && bResultNode && !bCompareNode) {
				return !level.bOmitClientNodesFromCount || !xml.isClientUrl(url);
			}

			return false;
		},

		insertInStore(/*QueryResult?*/qrNext) {
			// summary: add url to parent store if result node; add it before given next query result which could be
			// an action that is in place of an actual query result node
			var p = this.parentResult;
			if (p && p.store && this.bResultNode) {
				p.store.add(this.getNode(), qrNext ? {before: qrNext.getResultUrl()} : null);
				_updateGroupCount(p, 1, true);
			}
		},

		/** return true if a report row that is also a group */
		isGroupReport() {
			return this.bReport && this.bResultNode;
		},

		/** return true if a report row for its siblings (under same parent) */
		isSiblingReport() {
			return this.bReport && !this.bResultNode;
		},

		moveInStore(/*QueryResult?*/qrNext) {
			// summary: move result in parent store before qrNext if provided, otherwise to end
			var p = this.parentResult;
			if (p && p.store && this.bResultNode) {
				var newParent = qrNext && qrNext.parentResult;
				if (newParent && (newParent != p)) {
					// it's possible dragged into an empty group in which case qrNext is the next group, so
					// need to insert into the (empty) previous group
					if (p.isGroup() && !newParent.isGroup()) {
						newParent = newParent.getNextResult(qrNext, true);
					}

					// moving from one parent result to another
					if (newParent) {
						p.removeChild(this, true);
						this.parentResult = newParent;
						this.parentResult.addChild(this, qrNext);
						this.insertInStore(qrNext);
					}
				} else {
					// moving under same parent
					p.store.remove(this.url);
					p.store.add(this.getNode(), qrNext ? {before: qrNext.getResultUrl()} : null);
				}
			}
		},

		/**
		 * Return true if populated from the default group store
		 * @return {boolean}
		 */
		isDefaultGroup() {
			const {groupValue, level: {groupDefaultsStore}} = this;

			if (!groupDefaultsStore || !groupValue || !this.isGroup()) {
				return false;
			}

			const results = groupDefaultsStore.query({[groupDefaultsStore.urlProperty]: groupValue});
			return results.total === 1;
		},

		/**
		 * Return true if node is a type of divider
		 * @return {boolean}
		 */
		isDivider() {
			return reflection.isDividerType(this.getNode()?.$Type);
		},

		isFiltered() {
			// summary: return true if result was queried with filter conditions
			return this.store && this.store.bFiltered;
		},

		isGroup(/*Boolean*/bOmitActions) {
			// summary: return if this result is a group, and optionally check to see that this is not an action result at group level
			return !!this.level.groupSpec && (!bOmitActions || !this.bAction);
		},

		/**
		 * Return true if result is parent's only child
		 * @return {boolean}
		 */
		isOnlyChild() {
			const parent = this.parentResult;
			return parent && (parent.nChildren === 1);
		},

		isRoot() {
			// summary: return true if this is root query result for view
			return !this.parentResult;
		},

		isSorted() {
			// summary: return true if result was queried with sort directives
			return this.store && this.store.bSorted;
		},

		/**
		 * Return true for ordered tables
		 * @return {boolean}
		 */
		hasOrderedChildren() {
			return !!this.level.childLevel?.bOrdered;
		},

		predictChildCount(/*Number*/querySize) {
			// summary: predict how many child results exist for this result without creating store
			let predictedCount = querySize;

			if (this.isGroup()) {
				// if group, then get group count
				predictedCount = this.getGroupCount() ?? querySize;
			} else {
				// otherwise, if level expands to show immediate children, return number stored in attribute
				var childLevel = this.level.childLevel;
				if (childLevel && childLevel.resultPath) {
					var urls = this.getResultPathUrls();
					predictedCount = urls ? urls.length : 0;
				}
			}

			// if no hints, then just return querySize
			return Math.min(predictedCount, querySize);
		},

		preProcessContext(/*Context*/ctx) {
			// summary: set context to read-only if rules exist for level
			ctx.queryResult = this;

			if (ctx.isReadOnly()) {
				return;
			}

			var b = this.level.bReadOnly || xml.nodeIsFrozen(ctx.getNode());
			if (!b) {
				ctx.pushParams().setNodeURL(this.url);
				var actions = this.level.actions;
				var rules = actions && actions.m_rules;
				b = rules && !def.processRules(ctx, rules);
				ctx.pop();
			}

			if (b) {
				ctx.setReadOnly(true);
				this._bReadOnly = true;
			}
		},

		postProcessContext(/*Context*/ctx) {
			// summary: reset context read-only status if previously changed
			ctx.queryResult = null;

			if (this._bReadOnly) {
				ctx.setReadOnly(false);
				delete this._bReadOnly;
			}
		},

		/**
		 * Query for child results of this result
		 * @param {Record<string, any>} [query]
		 * @param {Record<string, any>} [queryOptions]
		 * @return {Promise<JsonResponse>}
		 */
		query(query, queryOptions) {
			const {rootValueStore} = this;

			// if this is root item and query has changed since view was first grouped, then need to recreate stores;
			// it would be great if GroupNodes/ValuesFromPairs/ValuePairs stores could handle this automatically based on
			// query and _lastQuery, but since the nested stores are queried for each group, _lastQuery can change frequently.
			// Ideally, maybe we should specify useCache as an option and then not track _lastQuery - only track it when
			// server actually queried.
			if (rootValueStore && rootValueStore.isQueryDirty(query)) {
				this.store = null;
			}

			const store = this.getStore();
			if (store) {
				const res = store.query(query, queryOptions);

				when(res, results => {
					const total = results.total;
					if (total != null) {
						// when group store is queried, update group count just in case initial count varies from actual count
						// (edits were made in between ValuesByXML and QueryByXML queries)
						const start = (queryOptions && queryOptions.start) || 0;
						const count = (queryOptions && queryOptions.count) || results.length;
						const totalRead = start + count;
						let bMore = total > totalRead;
						const totalMax = this.getChildCountLimit();

						// if total read is greater than allowed max, then no more to query
						if (bMore && totalMax && (totalRead >= totalMax)) {
							bMore = false;
						}

						this.bMore = bMore;

						if (this.isGroup(true) && !store.bQueryCountLimited) {
							_updateGroupCount(this, store.getResultTotal ? store.getResultTotal() : total);
						}
					}
				});


				return res;
			}

			return null;
		},

		removeChild(/*Result*/qr, /*Boolean?*/bPersistent) {
			// summary: remove child from parent and reduce count
			this.childResults.dele(qr);

			if (qr.bResultNode) {
				--this.nChildren;

				// update parent's store when result is removed
				if (bPersistent && this.store) {
					this.store.remove(qr.url);
					_updateGroupCount(this, -1, true);
				}
			}
		},

		/**
		 * Remove result id from data store of this query result, or any child results if grouped;
		 * it is assumed that there is not already a query result with this id - only that it is a
		 * result within the data store (and not a group)
		 * @param {string} id
		 * @return {Result} - return query result from which is was removed, if any
		 */
		removeChildResultWithId(id) {
			const {childResults, rootValueStore} = this;
			const hasChildren = childResults && childResults.length;
			const hasGroups = this.containsGroups();
			const store = this.getStore();
			let result;

			if (hasGroups) {
				// iterate through displayed groups
				if (hasChildren) {
					childResults.some(qr => {
						result = qr.removeChildResultWithId(id);
						return !!result;
					});

					if (result) {
						return result;
					}
				}

				// if there are more group results that are not yet displayed (and thus no child results),
				// then need to iterate through store results to find and remove result
				if (!result && (!hasChildren || this.hasMoreChildren())) {
					if (store.removeRef(id)) {
						// found reference in leaf group
						result = this;
					}
				}

				// if there are two levels of grouping, and the first level is not yet expanded, then find reference in ValuePairs store
				if (!result && rootValueStore) {
					const item = rootValueStore.removeRef(id);
					if (item) {
						const value = item[rootValueStore.value1Property] || xml.VALUE_BLANK;
						result = childResults.find(qr => qr.getGroupValue() === value);

						// if the group is not yet displayed, then the only thing to update is the overall table count
						result = result || this;
					}
				}
			}

			// simple case - look in store for reference, either at root level if no groups, or this is a leaf group with results directly below
			if (!result && store) {
				const index = store.remove(id);
				if (index >= 0) {
					result = this;
				}
			}

			// if reference found and removed, then subtract from group count
			if (result && result.isGroup(true)) {
				_updateGroupCount(result, -1, true);
			}

			return result;
		},

		renderActions(/*Context*/ctx, /*DOMNode*/domNode, /*Function*/onMenuAction) {
			// summary: render result actions
			var actions = this.level.actions;

			if (!actions || (this.bReport && !this.bResultNode)) {
				return;
			}

			var bForceDisabled = this.level.shouldDisableActions(ctx, this.getNode());
			if (bForceDisabled && !this.level.bRenderDisabledActions) {
				return;
			}

			this.resetActionContext(ctx);

			// Check for VIP actions
			_.each(actions.getActions(), function(a) {
				if (a.bEllipses ||
					(a.m_typeControl != def.ACTION_ICON && a.m_typeControl != def.ACTION_ICON_BUTTON) ||
					(!ACTIONS_EXCLUDED_FROM_ELLIPSES.has(a.getId()) && !ACTIONS_EXCLUDED_FROM_ELLIPSES.has(a.m_tag))) {
					a.requirePrivateNode = true;
					a.privateNode = null;
				}
			});

			actions.toHtml(ctx, domNode, this.level.bRenderDisabledActions, bForceDisabled);

			// if some actions belong in a menu, then render it
			var menuActions = _getMenuActions(actions);
			if (menuActions) {
				new InlineActionMenu(Automation.params(ctx.defView.getHtmlKey(), "actions", this.getAutomationId(), {
					actions: menuActions,
					bToggleOpen: true,
					"class": "csi-row-actions-menu",
					materialIcon: "more_horiz",
					showLabel: false,
					onAction: onMenuAction
				})).placeAt(domNode.firstChild, "first").startup();
			}
		},

		resetActionContext(/*Context*/ctx) {
			// summary: Actions/Rule tags do not apply to Actions/Action, so reset read-only status if applied
			if (this._bReadOnly) {
				ctx.setReadOnly(false);
				delete this._bReadOnly;
			}
		},

		resetChildren() {
			// summary: clear child references and count
			this.nChildren = 0;
			this.childResults = null;
			this.resetReporting();
		},

		/**
		 * Update group result with new group data
		 * @param {string} groupValue
		 * @param {string} [groupUrl] - value of ref attribute if grouping by ref (groupValue could be name of ref in this case) 
		 * @param {string[]} [groupRefs] - array of URLs to nodes having group value
		 */
		resetGroupValue(groupValue, groupUrl, groupRefs) {
			console.assert(this.groupValue != null);
			console.assert(!!this.parentResult);

			var store = this.parentResult.store;
			var item = store.get(this.url);
			item[store.refsProperty] = groupRefs;
			item[store.urlProperty] = groupUrl;
			item[store.valueProperty] = groupValue;

			this.groupValue = groupValue;
		},

		/**
		 * Reset any cached information used for reporting
		 */
		resetReporting() {
			this.childResultUrls = null;
		},

		resetStore(/*Boolean?*/bFull) {
			// summary: reset store so children will be queried again
			if (this.store && this.store.reset && !bFull) {
				this.store.reset();
			} else {
				delete this.store;
			}

			delete this.rootValueStore;
		}
	});

	_Result.excludeActionFromEllipses = function(/*string*/actionName) {
		// summary: add action to "Exclude from ellipses" list
		ACTIONS_EXCLUDED_FROM_ELLIPSES.add(actionName);
	};

	return _Result;
});